import { toast } from "@/components/ui/use-toast"

/**
 * Copies text to clipboard and shows a toast notification
 * @param text - The text to copy to clipboard
 * @param successMessage - Optional custom success message (defaults to "Copied to clipboard")
 * @param errorMessage - Optional custom error message (defaults to "Failed to copy to clipboard")
 * @returns Promise<boolean> - Returns true if successful, false if failed
 */
export async function copyToClipboard(
  text: string,
  successMessage = "Copied to clipboard",
  errorMessage = "Failed to copy to clipboard"
): Promise<boolean> {
  try {
    if (!navigator.clipboard) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        const successful = document.execCommand('copy')
        document.body.removeChild(textArea)

        if (successful) {
          toast({
            title: "Success",
            description: successMessage,
          })
          return true
        } else {
          throw new Error('execCommand failed')
        }
      } catch (err) {
        document.body.removeChild(textArea)
        throw err
      }
    } else {
      await navigator.clipboard.writeText(text)
      toast({
        title: "Success",
        description: successMessage,
      })
      return true
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error)
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    })
    return false
  }
}

/**
 * Creates a click handler function for copying text with toast notifications
 * @param getText - Function that returns the text to copy, or a static string
 * @param successMessage - Optional custom success message
 * @param errorMessage - Optional custom error message
 * @returns Click handler function
 */
export function createCopyClickHandler(
  getText: (() => string) | string,
  successMessage?: string,
  errorMessage?: string
) {
  return async (event?: Event) => {
    event?.preventDefault()
    const text = typeof getText === 'function' ? getText() : getText
    await copyToClipboard(text, successMessage, errorMessage)
  }
}

/**
 * Hook-style function for creating copy handlers (for consistency with React patterns)
 * @param text - The text to copy
 * @param options - Optional configuration object
 * @returns Object with copy function and click handler
 */
export function useCopyToClipboard(
  text: string,
  options: {
    successMessage?: string
    errorMessage?: string
  } = {}
) {
  const copy = () => copyToClipboard(text, options.successMessage, options.errorMessage)
  const handleCopyClick = createCopyClickHandler(text, options.successMessage, options.errorMessage)

  return {
    copy,
    handleCopyClick,
  }
}
